import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useContext, useState, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Listbox } from '@headlessui/react'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid'
import { APIContext } from 'context/APIContext'
import { TrainingCalendarContext } from 'context/TrainingCalendarContext'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import { buildTrainingTimelineDateRange } from 'components/Training/TrainingTimeline/Helpers/timelineConstants'
import { AppIsInTestEnvironment } from 'functions/environmentUtils';
import { commitSimulationClockDayOffset } from 'redux/slices/simulationClock'
import { ff, flags } from 'functions/roleBasedAccessUtils'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame';
import KinesisProfileBreadCrumbs from 'components/Profile/KinesisProfileBreadCrumbs'
import KinesisProfileHeaderV5 from 'components/Profile/KinesisProfileHeaderV5';
import ContentFrame from 'components/Frame/ContentFrame'
import ComplianceRadialDials from 'components/Reporting/ComplianceRadialDials'
import KinesisEventsList from 'components/Athlete/v2/KinesisEventsList'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import TrainingTimeline from 'components/Training/TrainingTimeline/TrainingTimeline'
import TrainingCalendar from 'components/Training/TrainingCalendar/TrainingCalendar'

export default function KinesisProfileV5({
    propProfileType = 'individual',
    kinesisProfileDetails = null
}) {
    const navigate = useNavigate()
    const { urlGroupId, urlAthleteId } = useParams()

    const { GetKinesisProfileWorkoutCompliance, GetKinesisProfileEvents, RegenerateActiveProgramVolume, RegenerateActiveProgramIncompleteWorkouts } = useContext(APIContext)
    const { GetTrainingTimelinePrograms, GetTrainingCalendarData, SaveWorkoutConfigWeekdayAssignmentOverride } = useContext(APIContext)

    const { trainingCalendarDateRange, trainingCalendarTimescaleOptions, UpdateTrainingCalendarTimescale } = useContext(TrainingCalendarContext)
    const { trainingCalendarTimescale: contextTrainingCalendarTimescale } = useContext(TrainingCalendarContext)

    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)
    const clientDate = moment().local().add(simulationClockDayOffset, 'days').format('YYYY-MM-DD')
    const clientWeekOfTheYearKey = new WeekOfTheYearKey(moment(clientDate, 'YYYY-MM-DD')).toString()

    const userType = useSelector((state) => state.user.type)

    const { exerciseHashMap } = useGetExercises()

    const trainingTimelineDateRange = useMemo(() => buildTrainingTimelineDateRange(clientDate, 36), [clientDate])

    const [trainingCalendarTimescale, setTrainingCalendarTimescale] = useState('week')
    const [kinesisProfileWorkoutCompliance, setKinesisProfileWorkoutCompliance] = useState(null)
    const [kinesisProfileEvents, setKinesisProfileEvents] = useState(null)
    const [trainingTimelinePrograms, setTrainingTimelinePrograms] = useState(null)
    const [trainingCalendarData, setTrainingCalendarData] = useState(null)
    const [selectedWeekOfTheYearKey, setSelectedWeekOfTheYearKey] = useState(clientWeekOfTheYearKey)
    const [presentWeekFocusKey, setPresentWeekFocusKey] = useState(0) // bumped to scroll the timeline back even when the present week is already selected
    const [regenerateVolumeLoading, setRegenerateVolumeLoading] = useState(false)
    const [regenerateWorkoutsLoading, setRegenerateWorkoutsLoading] = useState(false)

    const trainingTimelineLoadKeyRef = useRef(null)

    const isGroupIndividualProfile = propProfileType === 'individual' && kinesisProfileDetails?.kinesisUserGroupId != null
    const canEditKinesisProfileWeekDaySchedules = ff(flags.canEditKinesisProfileWeekDaySchedules, { profileType: propProfileType })

    const InitializeArrowKeyEventListeners = () => {
        window.addEventListener('keydown', HandleArrowKeyNavigation)
        window.addEventListener('keydown', HandleCalendarTimescaleShortcut)
        return () => {
            window.removeEventListener('keydown', HandleArrowKeyNavigation)
            window.removeEventListener('keydown', HandleCalendarTimescaleShortcut)
        }
    }
    
    const LoadKinesisProfileWorkoutCompliance = async () => {
        
        if (propProfileType === 'group') {

            setKinesisProfileWorkoutCompliance(null)

            const parsedUrlGroupId = urlGroupId != null ? parseInt(urlGroupId) : null

            const newKinesisProfileWorkoutCompliance = await GetKinesisProfileWorkoutCompliance(parsedUrlGroupId, clientDate)

            setKinesisProfileWorkoutCompliance(newKinesisProfileWorkoutCompliance)

        }

    }

    const LoadKinesisProfileEvents = async () => {

        setKinesisProfileEvents(null)

        const parsedUrlGroupId = urlGroupId != null ? parseInt(urlGroupId) : null
        const parsedUrlAthleteId = urlAthleteId != null ? parseInt(urlAthleteId) : null
        
        const newKinesisProfileEvents = await GetKinesisProfileEvents(parsedUrlGroupId, parsedUrlAthleteId)
        
        setKinesisProfileEvents(newKinesisProfileEvents)

    }

    const InitializeTrainingTimelinePrograms = async () => {
        if (trainingTimelineLoadKeyRef.current === clientWeekOfTheYearKey) {
            return
        }
        trainingTimelineLoadKeyRef.current = clientWeekOfTheYearKey

        setTrainingTimelinePrograms(null)

        let trainingTimelinePrograms = []

        if (propProfileType === 'group') {
            trainingTimelinePrograms = await GetTrainingTimelinePrograms(
                parseInt(urlGroupId),
                null, 
                trainingTimelineDateRange.start.format('YYYY-MM-DD'), 
                trainingTimelineDateRange.end.format('YYYY-MM-DD'), 
                clientDate
            )
        }

        if (propProfileType === 'individual') {
            trainingTimelinePrograms = await GetTrainingTimelinePrograms(
                null,
                parseInt(urlAthleteId), 
                trainingTimelineDateRange.start.format('YYYY-MM-DD'), 
                trainingTimelineDateRange.end.format('YYYY-MM-DD'), 
                clientDate
            )
        }
        
        setTrainingTimelinePrograms(trainingTimelinePrograms)
    }

    const UpdateTrainingCalendarTimescaleSelection = (timescale) => {
        setTrainingCalendarTimescale(timescale)
        UpdateTrainingCalendarTimescale(timescale)
    }

    const HandleArrowKeyNavigation = (e) => {
        if (
            e.key !== 'ArrowLeft'
            && e.key !== 'ArrowUp'
            && e.key !== 'ArrowRight'
            && e.key !== 'ArrowDown'
        ) {
            return
        }

        const tagName = e.target?.tagName
        
        if (
            tagName === 'INPUT'
            || tagName === 'TEXTAREA'
            || tagName === 'SELECT'
            || e.target?.isContentEditable
        ) {
            return
        }

        e.preventDefault()

        const weekDelta = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1

        setSelectedWeekOfTheYearKey((currentWeekOfTheYearKey) => (new WeekOfTheYearKey(currentWeekOfTheYearKey).add(weekDelta).toString()))
    }

    const HandleCalendarTimescaleShortcut = (e) => {
        if (
            e.shiftKey === false
            || e.metaKey || e.ctrlKey || e.altKey
            || (e.code !== 'KeyW' && e.code !== 'KeyM')
        ) {
            return
        }

        const tagName = e.target?.tagName
        
        if (
            tagName === 'INPUT'
            || tagName === 'TEXTAREA'
            || tagName === 'SELECT'
            || e.target?.isContentEditable
        ) {
            return
        }

        e.preventDefault()

        UpdateTrainingCalendarTimescaleSelection(e.code === 'KeyW' ? 'week' : 'block')
    }

    const JumpToPresentWeek = () => {
        setSelectedWeekOfTheYearKey(clientWeekOfTheYearKey)
        setPresentWeekFocusKey((currentKey) => currentKey + 1)
    }

    const NavigateCalendarToSpecificWeek = (specificWeekOfTheYearKey) => {
        setSelectedWeekOfTheYearKey(specificWeekOfTheYearKey.toString())
    }

    const LoadTrainingCalendarData = async (abortSignal) => {

        setTrainingCalendarData(null)

        const calendarStartDate = trainingCalendarDateRange[0]
        const calendarEndDate = trainingCalendarDateRange[trainingCalendarDateRange.length - 1]

        var parameterGroupId = urlGroupId
        if (propProfileType === 'individual') {
            parameterGroupId = kinesisProfileDetails?.kinesisUserGroupId ?? null
        }

        const newTrainingCalendarData = await GetTrainingCalendarData(parameterGroupId, urlAthleteId, calendarStartDate, calendarEndDate, clientDate, { signal: abortSignal })

        // A newer load replaced this one (week navigation, profile change, or StrictMode remount)

        if (abortSignal?.aborted || newTrainingCalendarData === null) return

        setTrainingCalendarData(newTrainingCalendarData)
    }

    const UpdateKinesisWorkoutCalendarItemDate = async (draggedKinesisWorkout, newDate) => {
        if (
            canEditKinesisProfileWeekDaySchedules === false
            || isGroupIndividualProfile === true
        ) {
            return
        }

        const currentDate = moment(draggedKinesisWorkout.date, 'YYYY-MM-DD')
        const currentDateString = draggedKinesisWorkout.date
        const newDateString = newDate.format('YYYY-MM-DD')

        // Workouts can only be moved to another day within their current ISO week

        if (
            currentDateString === newDateString
            || currentDate.isSame(newDate, 'isoWeek') === false
        ) {
            return
        }

        // Workouts saved before variant letters existed only have the letter at the end of the name ("<workout config name> <variant letter>")

        const workoutVariantLetter = draggedKinesisWorkout.workoutVariantLetter || draggedKinesisWorkout.name.split(' ').pop()

        // Optimistically move the workout, then roll back if the override fails to save

        MoveKinesisWorkoutCalendarItems(draggedKinesisWorkout, currentDateString, newDateString)

        const parsedUrlGroupId = urlGroupId != null ? parseInt(urlGroupId) : null
        const parsedUrlAthleteId = urlAthleteId != null ? parseInt(urlAthleteId) : null

        const result = await SaveWorkoutConfigWeekdayAssignmentOverride(parsedUrlGroupId, parsedUrlAthleteId, draggedKinesisWorkout.workoutConfigId, workoutVariantLetter, newDateString)

        if (!result) {
            console.error('Failed to save workout config weekday assignment override')
            MoveKinesisWorkoutCalendarItems({ ...draggedKinesisWorkout, date: newDateString }, newDateString, currentDateString)
        }
    }

    const MoveKinesisWorkoutCalendarItems = (draggedKinesisWorkout, fromDateString, toDateString) => {

        setTrainingCalendarData((currentTrainingCalendarData) => {

            if (currentTrainingCalendarData === null) return currentTrainingCalendarData

            return currentTrainingCalendarData.map((item) => {

                const isDraggedKinesisWorkout = (
                    item.calendarEventType === 'kinesis-workout'
                    && item.workoutId === draggedKinesisWorkout.workoutId
                    && item.date === fromDateString
                )

                // Group profile: move the group's incomplete individual athlete workouts along with the group workout

                const isGroupIndividualKinesisWorkout = (
                    propProfileType === 'group'
                    && draggedKinesisWorkout.userId === null
                    && item.calendarEventType === 'kinesis-workout'
                    && item.userId !== null
                    && item.completed !== 1
                    && item.name === draggedKinesisWorkout.name
                    && item.date === fromDateString
                )

                if (!isDraggedKinesisWorkout && !isGroupIndividualKinesisWorkout) return item

                return { ...item, date: toDateString }
            })
        })
    }

    const OnRegenerateActiveProgramVolumeButton = async () => {
        setRegenerateVolumeLoading(true)

        const parsedUrlGroupId = urlGroupId != null ? parseInt(urlGroupId) : null
        const parsedUrlAthleteId = urlAthleteId != null ? parseInt(urlAthleteId) : null

        const result = await RegenerateActiveProgramVolume(parsedUrlGroupId, parsedUrlAthleteId, clientDate)

        setRegenerateVolumeLoading(false)

        if (result) {
            window.location.reload()
        } else {
            console.error('Failed to regenerate active program volume')
        }
    }

    const OnRegenerateWorkoutsButton = async () => {

        setRegenerateWorkoutsLoading(true)

        const parsedUrlGroupId = urlGroupId != null ? parseInt(urlGroupId) : null
        const parsedUrlAthleteId = urlAthleteId != null ? parseInt(urlAthleteId) : null

        const result = await RegenerateActiveProgramIncompleteWorkouts(parsedUrlGroupId, parsedUrlAthleteId, clientDate)

        setRegenerateWorkoutsLoading(false)

        if (result) {
            window.location.reload()
        } else {
            console.error('Failed to regenerate workouts')
        }
    }

    useEffect(() => InitializeArrowKeyEventListeners(), [])
    useEffect(() => { UpdateTrainingCalendarTimescale(trainingCalendarTimescale) }, [])
    useEffect(() => { setSelectedWeekOfTheYearKey(clientWeekOfTheYearKey) }, [clientWeekOfTheYearKey])
    useEffect(() => { LoadKinesisProfileWorkoutCompliance() }, [urlGroupId])
    useEffect(() => { LoadKinesisProfileEvents() }, [urlGroupId])
    useEffect(() => { InitializeTrainingTimelinePrograms() }, [urlGroupId, urlAthleteId, clientDate])

    const kinesisProfileLoading = (
        kinesisProfileDetails === null
        || Object.keys(exerciseHashMap).length === 0
        || (propProfileType === 'group' && kinesisProfileWorkoutCompliance === null)
        || kinesisProfileEvents === null
        || trainingTimelinePrograms === null
    )

    const profileName = (
        kinesisProfileDetails === null ? null : 
            propProfileType === 'group' ? kinesisProfileDetails.name : 
                `${kinesisProfileDetails.firstName} ${kinesisProfileDetails.lastName}`
    )

    if (kinesisProfileLoading) {
        return (
            <ScreenFrame contentClassName="h-full flex flex-col">
                <div className='h-28 w-full bg-gray-200 rounded-lg animate-pulse' />

                <div className='flex-1 min-h-0 flex flex-col gap-4'>
                    <div className='flex-1 min-h-0 grid grid-cols-4 gap-4'>
                        <div className="flex flex-col bg-gray-200 rounded-lg animate-pulse" />

                        <div className='col-span-3 flex flex-col bg-gray-200 rounded-lg animate-pulse' />
                    </div>

                    <div className='flex-shrink-0 h-[270px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    const kinesisProfileGridContent = (
        <div className="grid min-h-0 flex-1 grid-cols-4 gap-3 2xl:gap-4">
            <div className="col-span-1 flex min-h-0 flex-col gap-3 2xl:gap-4">
                <KinesisProfileHeaderV5 
                    propProfileType={propProfileType} 
                    urlGroupId={urlGroupId}
                    urlAthleteId={urlAthleteId}
                    kinesisProfileDetails={kinesisProfileDetails} 
                    displaySettingsButton={isGroupIndividualProfile === false}
                />

                {propProfileType === 'group' && (
                    <div className='h-[260px]'>
                        <ContentFrame
                            className='h-full'
                            titleText="Compliance"
                            toolTipText={`Group compliance over the past ${4} weeks`}
                        >
                            <ComplianceRadialDials 
                                workoutCompliance={kinesisProfileWorkoutCompliance}
                                RADIAL_CHART_SIZE={260}
                                displayBottomStats={false} 
                            />
                        </ContentFrame>
                    </div>
                )} 

                <div className='min-h-0 flex-1'>
                    <KinesisEventsList 
                        boilerplateEnabled={1}
                        profileType={propProfileType}
                        groupId={urlGroupId}
                        athleteId={urlAthleteId}
                        allowEditing={isGroupIndividualProfile === false}
                        kinesisEvents={kinesisProfileEvents}
                        NavigateCalendarToSpecificWeek={NavigateCalendarToSpecificWeek}
                    />
                </div>
            </div> 

            <div className="col-span-3 flex min-h-0 flex-col gap-3 2xl:gap-4">
                <ContentFrame>
                    <TrainingTimeline
                        clientDate={clientDate}
                        clientWeekOfTheYearKey={clientWeekOfTheYearKey}
                        trainingTimelineStartDate={trainingTimelineDateRange.start}
                        trainingTimelineEndDate={trainingTimelineDateRange.end}
                        selectedWeekOfTheYearKey={selectedWeekOfTheYearKey}
                        setSelectedWeekOfTheYearKey={setSelectedWeekOfTheYearKey}
                        selectedWeekFocusKey={presentWeekFocusKey}
                        defaultShowMacroFocus={false}
                        trainingTimelinePrograms={trainingTimelinePrograms}
                        kinesisEvents={kinesisProfileEvents ?? []}
                    />
                </ContentFrame>

                <div className="min-h-0 flex-1 relative">
                    {/* Wait for the context to match so the calendar never mounts with a date range sized for another timescale */}
                    {contextTrainingCalendarTimescale === trainingCalendarTimescale && (
                        <TrainingCalendar 
                            clientDate={clientDate}
                            trainingCalendarTimescale={trainingCalendarTimescale}
                            propProfileType={propProfileType}
                            kinesisProfileDetails={kinesisProfileDetails}
                            urlGroupId={urlGroupId}
                            urlUserId={urlAthleteId}
                            selectedWeekOfTheYearKey={selectedWeekOfTheYearKey}
                            setSelectedWeekOfTheYearKey={setSelectedWeekOfTheYearKey}
                            trainingCalendarData={trainingCalendarData}
                            trainingCalendarDateRange={trainingCalendarDateRange}
                            LoadTrainingCalendarData={LoadTrainingCalendarData}
                            UpdateKinesisWorkoutCalendarItemDate={isGroupIndividualProfile ? null : UpdateKinesisWorkoutCalendarItemDate} // null: workouts aren't draggable
                        />
                    )}

                    {AppIsInTestEnvironment() && userType === 'super-admin' && (
                        <div className='absolute bottom-0 right-0 z-10 h-14 w-3/4 bg-gray-100 opacity-0 transition-opacity hover:opacity-100 p-2 flex items-center gap-x-2'>
                        {/* <div className='absolute bottom-0 right-0 z-10 h-14 w-3/4 bg-gray-100 p-2 flex items-center gap-x-2'> */}
                            <div className='flex-1'>
                                <BrandColorButton
                                    size='extra-small'
                                    text='-7 days'
                                    action={() => { commitSimulationClockDayOffset(simulationClockDayOffset - 7) }}
                                />
                            </div>
                            
                            <div className='flex-1'>
                                <BrandColorButton
                                    size='extra-small'
                                    text='-1 day'
                                    action={() => { commitSimulationClockDayOffset(simulationClockDayOffset - 1) }}
                                />
                            </div>

                            <div className='flex-1'>
                                <BrandColorButton
                                    size='extra-small'
                                    text='+1 day'
                                    action={() => { commitSimulationClockDayOffset(simulationClockDayOffset + 1) }}
                                />
                            </div>

                            <div className='flex-1'>
                                <BrandColorButton
                                    size='extra-small'
                                    text='+7 days'
                                    action={() => { commitSimulationClockDayOffset(simulationClockDayOffset + 7) }}
                                />
                            </div>

                            <div className='w-40'>
                                <BrandColorButton
                                    size='extra-small'
                                    text="Regen Volume"
                                    action={OnRegenerateActiveProgramVolumeButton}
                                    isLoading={regenerateVolumeLoading}
                                    className='shadow-md'
                                />
                            </div>
                            
                            <div className='w-40'>
                                <BrandColorButton
                                    size='extra-small'
                                    text="Regen Workouts"
                                    action={OnRegenerateWorkoutsButton}
                                    isLoading={regenerateWorkoutsLoading}
                                    className='shadow-md'
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )

    return (
        <ScreenFrame contentClassName="h-full">
            <div className='flex items-center justify-between gap-x-2 pl-2 -my-0.5'>
                <KinesisProfileBreadCrumbs 
                    propProfileType={propProfileType} 
                    profileName={profileName} 
                    showSettingsCrumb={false} 
                />

                <div className='flex-1'/>

                {isGroupIndividualProfile && (
                    <div
                        className='h-8 -my-1 px-3 flex items-center rounded-md bg-orange-400 text-sm text-white whitespace-nowrap cursor-pointer'
                        onClick={() => navigate(`/group/${kinesisProfileDetails.kinesisUserGroupId}`)}
                    >
                        Athlete currently in group.&nbsp;<span className='font-semibold underline text-sm'>View group</span>
                    </div>
                )}

                <button
                    type='button'
                    className='-my-1 flex h-8 items-center cursor-pointer rounded-md border border-gray-300 bg-white px-3 shadow-md whitespace-nowrap hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500'
                    onClick={JumpToPresentWeek}
                >
                    <span className='text-sm font-normal text-gray-700'>Today</span>
                </button>

                <Listbox 
                    value={trainingCalendarTimescale} 
                    onChange={UpdateTrainingCalendarTimescaleSelection}
                >
                    <div className='relative w-44 -my-1'>
                        <Listbox.Button className='relative flex h-8 w-full items-center cursor-pointer rounded-md border border-gray-300 bg-white pl-2 pr-7 text-left shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500'>
                            <span className='block truncate text-sm text-gray-700'>
                                {trainingCalendarTimescaleOptions.find((option) => option.value === trainingCalendarTimescale)?.label}
                            </span>

                            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5'>
                                <ChevronUpDownIcon className='h-4 w-4 text-gray-400' />
                            </span>
                        </Listbox.Button>

                        <Listbox.Options className='absolute right-0 z-[60] mt-1 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-none'>
                            {trainingCalendarTimescaleOptions.map((option) => (
                                <Listbox.Option
                                    key={option.value}
                                    value={option.value}
                                    className={({ active }) => `relative cursor-pointer select-none py-1.5 pl-2 pr-8 text-sm ${active ? 'bg-orange-50' : ''}`}
                                >
                                    {({ selected }) => (
                                        <>
                                            <span className={`block truncate text-sm ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                {option.label}
                                            </span>

                                            {selected && (
                                                <span className='absolute inset-y-0 right-0 flex items-center pr-2 text-orange-600'>
                                                    <CheckIcon className='h-4 w-4' />
                                                </span>
                                            )}
                                        </>
                                    )}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </div>
                </Listbox>
            </div>

            <div className="flex h-[calc(100%+0.5rem)] min-h-0 flex-col gap-4 overflow-hidden pb-2 -mb-2">
                {kinesisProfileGridContent}
            </div>
        </ScreenFrame>
    )
}
